import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  CircularProgress,
  Alert as MuiAlert,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Psychology as TherapistIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';

/**
 * AlertsView Component for Clinical Supervisors
 *
 * Displays all pending alerts for clients under supervised therapists
 * with filtering and sorting capabilities
 */
function AlertsView({ token }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [therapistFilter, setTherapistFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    if (token) {
      fetchAlerts();
    }
  }, [token]);

  const fetchAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await clinicalSupervisorApi.getPendingAlerts(token);
      setAlerts(response.alerts || []);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const toggleRowExpand = (alertId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return 'error';
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getAlertTypeLabel = (type) => {
    switch (type) {
      case 'CARE_NOTE_CONCERN':
        return 'Care Note';
      case 'CRISIS_ALERT':
        return 'Crisis';
      case 'SAFETY_ISSUE':
        return 'Safety';
      default:
        return type;
    }
  };

  // Get unique therapists for filter
  const uniqueTherapists = [...new Set(
    alerts
      .map(alert => alert.therapist || alert.therapistId)
      .filter(Boolean)
      .map(t => JSON.stringify({ id: t._id, name: t.name }))
  )].map(str => JSON.parse(str));

  // Filter and sort alerts
  const filteredAndSortedAlerts = alerts
    .filter(alert => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const clientName = alert.clientId?.name?.toLowerCase() || '';
        const therapistName = (alert.therapist?.name || alert.therapistId?.name || '').toLowerCase();
        if (!clientName.includes(search) && !therapistName.includes(search)) {
          return false;
        }
      }

      // Priority filter
      if (priorityFilter !== 'all' && alert.priority !== priorityFilter) {
        return false;
      }

      // Type filter
      if (typeFilter !== 'all' && alert.type !== typeFilter) {
        return false;
      }

      // Therapist filter
      if (therapistFilter !== 'all') {
        const alertTherapistId = alert.therapist?._id || alert.therapistId?._id;
        if (alertTherapistId?.toString() !== therapistFilter) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'priority':
          // Includes critical, which used to rank 0 and sort last (PD-01).
          const priorityOrder = { critical: 5, urgent: 4, high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'client':
          aValue = a.clientId?.name || '';
          bValue = b.clientId?.name || '';
          break;
        case 'therapist':
          aValue = a.therapist?.name || a.therapistId?.name || '';
          bValue = b.therapist?.name || b.therapistId?.name || '';
          break;
        default:
          return 0;
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

  // Summary statistics
  const stats = {
    total: alerts.length,
    critical: alerts.filter(a => a.priority === 'critical').length,
    urgent: alerts.filter(a => a.priority === 'urgent').length,
    high: alerts.filter(a => a.priority === 'high').length,
    medium: alerts.filter(a => a.priority === 'medium').length,
    low: alerts.filter(a => a.priority === 'low').length
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <MuiAlert severity="error">{error}</MuiAlert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NotificationsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight="bold">
            Team Alerts
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<FilterIcon />} onClick={fetchAlerts}>
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Total Alerts</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Card sx={{ backgroundColor: 'rgba(211, 47, 47, 0.2)', border: 2, borderColor: 'error.main' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">CRITICAL</Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main">{stats.critical}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Card sx={{ backgroundColor: 'rgba(211, 47, 47, 0.1)' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Urgent</Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main">{stats.urgent}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Card sx={{ backgroundColor: 'rgba(237, 108, 2, 0.1)' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">High</Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.high}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Card sx={{ backgroundColor: 'rgba(237, 108, 2, 0.05)' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Medium</Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.medium}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Low</Typography>
              <Typography variant="h4" fontWeight="bold" color="info.main">{stats.low}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by client or therapist..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="CARE_NOTE_CONCERN">Care Note Concern</MenuItem>
                  <MenuItem value="CRISIS_ALERT">Crisis Alert</MenuItem>
                  <MenuItem value="SAFETY_ISSUE">Safety Issue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Therapist</InputLabel>
                <Select
                  value={therapistFilter}
                  label="Therapist"
                  onChange={(e) => setTherapistFilter(e.target.value)}
                >
                  <MenuItem value="all">All Therapists</MenuItem>
                  {uniqueTherapists.map(therapist => (
                    <MenuItem key={therapist.id} value={therapist.id}>
                      {therapist.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Alerts Table */}
      {filteredAndSortedAlerts.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotificationsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {alerts.length === 0 ? 'No pending alerts' : 'No alerts match your filters'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell width="50px"></TableCell>
                <TableCell sortDirection={orderBy === 'priority' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'priority'}
                    direction={orderBy === 'priority' ? order : 'asc'}
                    onClick={() => handleRequestSort('priority')}
                  >
                    Priority
                  </TableSortLabel>
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell sortDirection={orderBy === 'client' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'client'}
                    direction={orderBy === 'client' ? order : 'asc'}
                    onClick={() => handleRequestSort('client')}
                  >
                    Client
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'therapist' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'therapist'}
                    direction={orderBy === 'therapist' ? order : 'asc'}
                    onClick={() => handleRequestSort('therapist')}
                  >
                    Therapist
                  </TableSortLabel>
                </TableCell>
                <TableCell>Concern</TableCell>
                <TableCell sortDirection={orderBy === 'createdAt' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'asc'}
                    onClick={() => handleRequestSort('createdAt')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedAlerts.map((alert) => {
                const therapistId = alert.therapistId?._id || alert.therapistId;
                const clientId = alert.clientId?._id || alert.clientId;

                return (
                <React.Fragment key={alert._id}>
                  <TableRow
                    hover
                    sx={{
                      backgroundColor: alert.priority === 'critical' ? 'rgba(211, 47, 47, 0.1)' :
                                       alert.priority === 'urgent' ? 'rgba(211, 47, 47, 0.05)' :
                                       'transparent'
                    }}
                  >
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpand(alert._id)}
                      >
                        {expandedRows.has(alert._id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View client page">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (therapistId && clientId) {
                              navigate(`/clinical-supervisor/therapists/${therapistId}/clients/${clientId}`);
                            }
                          }}
                          sx={{
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={alert.priority === 'urgent' || alert.priority === 'high' ? <ErrorIcon /> : <WarningIcon />}
                        label={alert.priority}
                        color={getPriorityColor(alert.priority)}
                        size="small"
                        sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getAlertTypeLabel(alert.type)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {alert.clientId?.name || 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TherapistIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {alert.therapist?.name || alert.therapistId?.name || 'Unassigned'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {alert.concern?.concernType?.replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={format(new Date(alert.createdAt), 'PPpp')}>
                        <Typography variant="body2">
                          {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row */}
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0 }}>
                      <Collapse in={expandedRows.has(alert._id)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                Concern Details
                              </Typography>
                              <Typography variant="body2" paragraph>
                                {alert.concern?.explanation || 'No explanation provided'}
                              </Typography>
                            </Grid>
                            {alert.concern?.suggestedAction && (
                              <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Suggested Action
                                </Typography>
                                <Chip
                                  label={alert.concern.suggestedAction.replace(/_/g, ' ')}
                                  size="small"
                                  color="primary"
                                />
                              </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                <strong>Client Email:</strong> {alert.clientId?.email || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                <strong>Therapist Email:</strong> {alert.therapist?.email || alert.therapistId?.email || 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default AlertsView;
